import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@lms/shared-types';
import { ConfirmModal, Loader, useToast } from '@lms/ui';
import { SearchInput } from '@/components/SearchInput';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '@/lib/api';
import { matchesSearch } from '@/lib/search-normalize';
import { questionBankSuccessToast, roleActionErrorToast } from '@/lib/user-feedback';

interface QuestionBank {
  id: string;
  title: string;
  description?: string | null;
  questionsCount?: number;
  assessmentsCount?: number;
  _count?: { questions?: number; assessments?: number };
}

const BANK_ICONS = ['factory', 'science', 'gavel', 'school', 'psychology', 'menu_book', 'calculate', 'folder'];

function bankIcon(title: string) {
  let hash = 0;
  for (let i = 0; i < title.length; i++) hash = Math.abs((hash << 5) - hash + title.charCodeAt(i));
  return BANK_ICONS[hash % BANK_ICONS.length];
}

function bankQuestionCount(bank: QuestionBank) {
  return bank.questionsCount ?? bank._count?.questions ?? 0;
}

function bankAssessmentCount(bank: QuestionBank) {
  return bank.assessmentsCount ?? bank._count?.assessments ?? 0;
}

function BankCard({
  bank,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
}: {
  bank: QuestionBank;
  canEdit: boolean;
  canDelete: boolean;
  onEdit: (bank: QuestionBank) => void;
  onDelete: (bank: QuestionBank) => void;
}) {
  const qCount = bankQuestionCount(bank);
  const aCount = bankAssessmentCount(bank);
  const isDeletable = qCount === 0 && aCount === 0;
  const lockTitle =
    qCount > 0
      ? 'No se puede eliminar porque contiene preguntas'
      : aCount > 0
        ? 'No se puede eliminar porque está en una evaluación'
        : 'En uso';

  return (
    <div className="lms-banks-page__card group">
      <div>
        <div className="lms-banks-page__card-top">
          <div className="lms-banks-page__card-icon">
            <span className="material-symbols-outlined" aria-hidden="true">
              {bankIcon(bank.title)}
            </span>
          </div>
          {(canEdit || canDelete) && (
            <div className="flex items-center gap-1">
              {canEdit && (
                <button
                  type="button"
                  className="lms-banks-page__card-action"
                  title="Editar banco"
                  aria-label={`Editar ${bank.title}`}
                  onClick={() => onEdit(bank)}
                >
                  <span className="material-symbols-outlined text-[20px]" aria-hidden="true">
                    edit
                  </span>
                </button>
              )}
              {canDelete &&
                (isDeletable ? (
                  <button
                    type="button"
                    className="lms-banks-page__card-action lms-banks-page__card-action--danger"
                    title="Eliminar banco"
                    aria-label={`Eliminar ${bank.title}`}
                    onClick={() => onDelete(bank)}
                  >
                    <span className="material-symbols-outlined text-[20px]" aria-hidden="true">
                      delete
                    </span>
                  </button>
                ) : (
                  <span className="lms-banks-page__card-action lms-banks-page__card-action--disabled" title={lockTitle} aria-label="En uso">
                    <span className="material-symbols-outlined text-[20px]" aria-hidden="true">
                      lock
                    </span>
                  </span>
                ))}
            </div>
          )}
        </div>

        <h3 className="lms-banks-page__card-title" title={bank.title}>
          {bank.title}
        </h3>

        <p className="lms-banks-page__card-desc">{bank.description || 'Sin descripción asignada.'}</p>
      </div>

      <div className="lms-banks-page__card-footer">
        <span className="lms-banks-page__badge">
          <span className="material-symbols-outlined text-[16px]" aria-hidden="true">
            quiz
          </span>
          {qCount} {qCount === 1 ? 'pregunta' : 'preguntas'}
        </span>
        <Link to={`/preguntas?bankId=${bank.id}`} className="lms-banks-page__link">
          Ver
          <span className="material-symbols-outlined text-[18px]" aria-hidden="true">
            arrow_forward
          </span>
        </Link>
      </div>
    </div>
  );
}

export function QuestionBanksPage() {
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission(PERMISSIONS.PREGUNTAS_CREAR);
  const canEdit = hasPermission(PERMISSIONS.PREGUNTAS_EDITAR);
  const canDelete = hasPermission(PERMISSIONS.PREGUNTAS_ELIMINAR);

  const [search, setSearch] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [editingBank, setEditingBank] = useState<QuestionBank | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [deletingBank, setDeletingBank] = useState<QuestionBank | null>(null);
  const { toast } = useToast();

  const { data, isLoading, error } = useQuery({
    queryKey: ['question-banks'],
    queryFn: () => api<{ data: QuestionBank[] }>('/questions/banks'),
  });

  const createBank = useMutation({
    mutationFn: () =>
      api('/questions/banks', {
        method: 'POST',
        body: {
          title: title.trim(),
          description: description.trim() || undefined,
        },
      }),
    onSuccess: () => {
      setTitle('');
      setDescription('');
      toast(questionBankSuccessToast('create'));
      queryClient.invalidateQueries({ queryKey: ['question-banks'] });
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo crear el banco de preguntas'));
    },
  });

  const updateBank = useMutation({
    mutationFn: () =>
      api(`/questions/banks/${editingBank!.id}`, {
        method: 'PATCH',
        body: {
          title: editTitle.trim(),
          description: editDescription.trim() || null,
        },
      }),
    onSuccess: () => {
      setEditingBank(null);
      toast(questionBankSuccessToast('edit'));
      queryClient.invalidateQueries({ queryKey: ['question-banks'] });
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo guardar el banco de preguntas'));
    },
  });

  const deleteBank = useMutation({
    mutationFn: () => api(`/questions/banks/${deletingBank!.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setDeletingBank(null);
      toast(questionBankSuccessToast('delete'));
      queryClient.invalidateQueries({ queryKey: ['question-banks'] });
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo eliminar el banco de preguntas'));
    },
  });

  useEffect(() => {
    if (!editingBank) return;
    setEditTitle(editingBank.title);
    setEditDescription(editingBank.description ?? '');
  }, [editingBank]);

  const banks = useMemo(() => {
    const rows = data?.data ?? [];
    return rows.filter((bank) => matchesSearch([bank.title, bank.description].filter(Boolean).join(' '), search));
  }, [data?.data, search]);

  const hasSearch = search.trim().length > 0;

  function handleCreate(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) {
      toast({ title: 'El nombre del banco es obligatorio.', variant: 'danger' });
      return;
    }
    createBank.mutate();
  }

  function handleEditSubmit(e: FormEvent) {
    e.preventDefault();
    if (!editTitle.trim()) {
      toast({ title: 'El nombre del banco es obligatorio.', variant: 'danger' });
      return;
    }
    updateBank.mutate();
  }

  return (
    <div className="lms-banks-page lms-site-admin-hub-page font-sans">
      <header className="mb-6 flex items-start gap-4">
        <HubBackLink to="/preguntas" label="Volver a Bancos de Preguntas" />
        <div className="min-w-0">
          <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Bancos de Preguntas</h1>
          <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
            Organiza preguntas en bancos reutilizables
          </p>
        </div>
      </header>

      <div className="lms-banks-page__grid-layout">
        <div className="lms-banks-page__col-create">
          <div className="lms-banks-page__create-card">
            <h2 className="lms-banks-page__create-title">Crear banco</h2>
            <form className="lms-banks-page__form" onSubmit={handleCreate}>
              <div className="lms-banks-page__form-group">
                <label htmlFor="bank-title" className="lms-banks-page__label">
                  Nombre <span className="lms-banks-page__label-required">*</span>
                </label>
                <input
                  id="bank-title"
                  type="text"
                  name="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Ej. Matemáticas Básicas"
                  required
                  className="lms-banks-page__input"
                />
              </div>

              <div className="lms-banks-page__form-group">
                <label htmlFor="bank-description" className="lms-banks-page__label">
                  Descripción
                </label>
                <textarea
                  id="bank-description"
                  name="description"
                  rows={4}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Breve descripción del contenido del banco..."
                  className="lms-banks-page__textarea"
                />
              </div>

              {canCreate && (
                <button type="submit" className="lms-banks-page__submit-btn" disabled={createBank.isPending}>
                  <span className="material-symbols-outlined text-[20px]" style={{ fontVariationSettings: "'FILL' 1" }} aria-hidden="true">
                    add_circle
                  </span>
                  Crear banco{createBank.isPending ? '…' : ''}
                </button>
              )}
            </form>
          </div>
        </div>

        <div className="lms-banks-page__col-list">
          <div className="lms-banks-page__list-header">
            <h2 className="lms-banks-page__list-title">Bancos Existentes</h2>
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Buscar banco..."
              aria-label="Buscar banco"
              className="lms-banks-page__search-wrap"
            />
          </div>

          {error && (
            <p className="mb-4 text-sm text-red-600" role="alert">
              {(error as Error).message}
            </p>
          )}

          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader />
            </div>
          ) : banks.length === 0 ? (
            <div className="lms-banks-page__empty">
              <span className="material-symbols-outlined lms-banks-page__empty-icon" aria-hidden="true">
                folder_off
              </span>
              <p className="lms-banks-page__empty-title">
                {hasSearch ? 'No se encontraron bancos con ese criterio' : 'Sin bancos creados'}
              </p>
              <p className="lms-banks-page__empty-text">
                {hasSearch
                  ? 'Intenta con otro término de búsqueda.'
                  : 'Crea tu primer banco usando el formulario de la izquierda.'}
              </p>
            </div>
          ) : (
            <div className="lms-banks-page__cards-grid">
              {banks.map((bank) => (
                <BankCard
                  key={bank.id}
                  bank={bank}
                  canEdit={canEdit}
                  canDelete={canDelete}
                  onEdit={setEditingBank}
                  onDelete={setDeletingBank}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {editingBank ? (
        <div
          className="lms-builder-settings-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-bank-dialog-title"
        >
          <div className="lms-builder-settings-modal__panel">
            <div className="lms-builder-settings-modal__head">
              <h3 id="edit-bank-dialog-title" className="lms-builder-settings-modal__title">
                Editar banco
              </h3>
              <button
                type="button"
                className="lms-builder-settings-modal__close"
                onClick={() => setEditingBank(null)}
                aria-label="Cerrar"
              >
                ✕
              </button>
            </div>
            <form id="edit-bank-form" className="lms-builder-settings-modal__body" onSubmit={handleEditSubmit}>
              <div className="form-field">
                <label className="form-label" htmlFor="edit-bank-title">
                  Nombre
                </label>
                <input
                  id="edit-bank-title"
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  placeholder="Ej. Matemáticas Básicas"
                  required
                  className="form-input-base"
                />
              </div>
              <div className="form-field">
                <label className="form-label" htmlFor="edit-bank-description">
                  Descripción
                </label>
                <textarea
                  id="edit-bank-description"
                  rows={4}
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  placeholder="Descripción del banco"
                  className="form-input-base"
                />
              </div>
            </form>
            <div className="lms-builder-settings-modal__footer">
              <button
                type="button"
                className="lms-builder-settings-modal__btn-ghost"
                onClick={() => setEditingBank(null)}
              >
                Cerrar
              </button>
              <button
                type="submit"
                form="edit-bank-form"
                className="lms-builder-settings-modal__btn-primary"
                disabled={updateBank.isPending}
              >
                {updateBank.isPending ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <ConfirmModal
        open={Boolean(deletingBank)}
        title="Eliminar banco"
        description={
          deletingBank ? `¿Está seguro de eliminar el banco «${deletingBank.title}»? Esta acción no se puede deshacer.` : ''
        }
        confirmLabel="Eliminar"
        loading={deleteBank.isPending}
        onConfirm={() => deleteBank.mutate()}
        onClose={() => {
          if (!deleteBank.isPending) setDeletingBank(null);
        }}
      />
    </div>
  );
}
